import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { RequestAutomation } from '@app/src/types/automations'
import { SortOrder } from '@app/src/types/filter'
import type { Request } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

interface SelectTemplateScrapingProps {
  name: string
  checkExistingAutomations?: boolean
}

const SelectTemplateForScraping: React.FC<SelectTemplateScrapingProps> = ({
  name,
  checkExistingAutomations = false,
}) => {
  const { worldfavorStandardTemplatesThatCanBeScreened } = useFlags()
  const { control } = useFormContext()
  const { formatMessage } = useIntl()

  const { items: automations } = useFetchCollectionWithPost<RequestAutomation>({
    payload: {
      filter: [],
      include: ['templateAutomations.template'],
    },
    key: FetchKey.Automation,
    endpoint: endpoints.automations,
    options: {
      enabled: checkExistingAutomations,
    },
  })

  const exisingAutomationForTemplateIds = automations
    .filter(automation => automation.automationType === RequestAutomationType.AI_SCREEN)
    .map(automation => automation.templateAutomations.map(templateAutomation => templateAutomation.template.id))
    .flat()

  const canScreenAllStandardTemplates =
    worldfavorStandardTemplatesThatCanBeScreened.length === 1 &&
    worldfavorStandardTemplatesThatCanBeScreened[0].toLowerCase() === 'all'

  return (
    <Select<{ title: string; id: number }, Request>
      name={name}
      fieldLabel={formatMessage({ id: 'form.createRequest.selectTemplateLabel' })}
      control={control}
      required
      enableAutoSelect
      multiple
      forceFetch
      navigation={{
        url: endpoints.worldfavorTemplates,
        type: 'post',
        postObject: {
          include: [],
          filter: [
            ...insertIf(!canScreenAllStandardTemplates, {
              name: 'title',
              filters: [
                {
                  value: worldfavorStandardTemplatesThatCanBeScreened,
                  operator: Operators.In,
                },
              ],
            }),
          ],
          sort: { target: 'title', order: SortOrder.ASCENDING },
        },
      }}
      findSelectedValue={(value, option): boolean => value?.id === option?.value?.id}
      objectToOption={(template: Request) => ({
        label: template.title,
        value: { title: template.title, id: template.id },
        disabled: checkExistingAutomations && exisingAutomationForTemplateIds.includes(template.id),
        adornment:
          checkExistingAutomations && exisingAutomationForTemplateIds.includes(template.id)
            ? formatMessage({ id: 'form.createRequest.automationExists' })
            : undefined,
      })}
    />
  )
}

export default SelectTemplateForScraping
